<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="highlightedMessage" name="Highlighted Message" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :default-width="1280" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card title="Style" class="mb-3">
			<b-form-row>
				<b-col v-for="opt in styleOptions" :key="opt.value" cols="6" sm="3" class="mb-2">
					<div
						class="style-option"
						:class="{ active: settings.style === opt.value }"
						@click="settings.style = opt.value"
						role="button"
					>
						<div :class="`style-thumb style-thumb--${opt.value}`">
							<div class="st-title"></div>
							<div class="st-body"></div>
						</div>
						<div class="style-label">{{ opt.text }}</div>
					</div>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Alignment" class="mb-3">
			<b-form-row>
				<b-col cols="7">
					<b-form-group label="Horizontal">
						<b-button-group class="d-flex w-100">
							<b-button v-b-tooltip.hover.bottom title="Align left"   :pressed="settings.textAlign == 'left'"   @click="settings.textAlign = 'left'"   variant="outline-secondary"><b-icon icon="align-start"  aria-hidden="true"></b-icon></b-button>
							<b-button v-b-tooltip.hover.bottom title="Align center" :pressed="settings.textAlign == 'center'" @click="settings.textAlign = 'center'" variant="outline-secondary"><b-icon icon="align-center" aria-hidden="true"></b-icon></b-button>
							<b-button v-b-tooltip.hover.bottom title="Align right"  :pressed="settings.textAlign == 'right'"  @click="settings.textAlign = 'right'"  variant="outline-secondary"><b-icon icon="align-end"    aria-hidden="true"></b-icon></b-button>
						</b-button-group>
					</b-form-group>
				</b-col>
				<b-col cols="5">
					<b-form-group label="Vertical">
						<b-button-group class="d-flex w-100">
							<b-button v-b-tooltip.hover.bottom title="Align top"    :pressed="settings.verticalAlign == 'top'"    @click="settings.verticalAlign = 'top'"    variant="outline-secondary"><b-icon icon="align-top"    aria-hidden="true"></b-icon></b-button>
							<b-button v-b-tooltip.hover.bottom title="Align middle" :pressed="settings.verticalAlign == 'middle'" @click="settings.verticalAlign = 'middle'" variant="outline-secondary"><b-icon icon="align-middle" aria-hidden="true"></b-icon></b-button>
							<b-button v-b-tooltip.hover.bottom title="Align bottom" :pressed="settings.verticalAlign == 'bottom'" @click="settings.verticalAlign = 'bottom'" variant="outline-secondary"><b-icon icon="align-bottom" aria-hidden="true"></b-icon></b-button>
						</b-button-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Shape" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Left padding">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model.number="settings.paddingLeft"/>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group :label="settings.verticalAlign === 'top' ? 'Top padding' : 'Bottom padding'">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model.number="settings.paddingBottom"/>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Right padding">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model.number="settings.paddingRight"/>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Animation" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="In Transition">
						<b-form-select v-model="settings.inTransition" :options="inTransitions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Out Transition">
						<b-form-select v-model="settings.outTransition" :options="outTransitions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Duration">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="5000" v-model.number="settings.duration"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<text-settings :settings="settings" no-align />
		<b-card title="Colors" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Title text">
						<color-picker v-model="settings.titleColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Title background">
						<color-picker v-model="settings.titleBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Message text">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Message background">
						<color-picker v-model="settings.textBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Gift text">
						<color-picker v-model="settings.giftColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Gift background">
						<color-picker v-model="settings.giftBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Outline &amp; Shadow" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Blur">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model.number="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Spread">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="50" step="any" v-model.number="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-group label="Extra CSS" class="mb-0">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		<template #preview>
			<highlighted-message-widget-preview :settings="settings || {}" />
		</template>
	</widget-settings-layout>
</template>

<script>
import animateCssTransitions from '../../animateCssTransitions';
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import HighlightedMessageWidgetPreview from '@/components/Widget/HighlightedMessageWidgetPreview.vue';

export default {
	components: {
		ColorPicker,
		WidgetSettingsLayout,
		TextSettings,
		HighlightedMessageWidgetPreview,
	},
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		settings: null,
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		savedSettingsJson: null,
		styleOptions: [
			{ value: 'bubble',  text: 'Bubble'  },
			{ value: 'blocks',  text: 'Blocks'  },
			{ value: 'minimal', text: 'Minimal' },
			{ value: 'classic', text: 'Classic' },
		],
		inTransitions:  [{ text: 'No transition', value: null }, ...animateCssTransitions.inTransitions],
		outTransitions: [{ text: 'No transition', value: null }, ...animateCssTransitions.outTransitions],
	}; },
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.saveError = null;
			this.error = null;
			this.loading++;
			const s = await this.$api.get('widgetSettings/highlighted-message');
			this.settings = {
				style:             s.style             || 'bubble',
				verticalAlign:     s.verticalAlign     || 'bottom',
				textAlign:         s.textAlign         || 'left',
				inTransition:      s.inTransition      || null,
				outTransition:     s.outTransition     || null,
				duration:          s.duration          || 500,
				titleColor:        s.titleColor        || 'rgba(255, 255, 255, 1)',
				titleBg:           s.titleBg           || 'rgba(0, 128, 0, 1)',
				textColor:         s.textColor         || 'rgba(255, 255, 255, 1)',
				textBg:            s.textBg            || 'rgba(0, 128, 0, 1)',
				giftColor:         s.giftColor         || 'rgba(0, 0, 0, 1)',
				giftBg:            s.giftBg            || 'rgba(255, 195, 0, 1)',
				fontFamily:        s.fontFamily        || '',
				fontSize:          s.fontSize          || 20,
				letterSpacing:     s.letterSpacing     || 0,
				lineHeight:        s.lineHeight        || 0,
				boldText:          s.boldText          || false,
				italicText:        s.italicText        || false,
				underlineText:     s.underlineText     || false,
				strikethroughText: s.strikethroughText || false,
				uppercaseText:     s.uppercaseText     || false,
				paddingLeft:       s.paddingLeft       || 0,
				paddingBottom:     s.paddingBottom     || 0,
				paddingRight:      s.paddingRight      || 0,
				blurRadius:        s.blurRadius        || 0,
				spreadRadius:      s.spreadRadius      || 0,
				shadowColor:       s.shadowColor       || 'rgba(0, 0, 0, 1)',
				extraCSS:          s.extraCSS          || '',
				customFont:        s.customFont        || false,
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if (this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saveError = null;
				this.saving++;
				await this.$api.post('widgetSettings/highlighted-message', { ...this.settings });
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>

<style scoped>
.style-option {
	cursor: pointer;
	padding: 4px;
	border: 2px solid transparent;
	border-radius: 8px;
	transition: border-color .15s, box-shadow .15s;
	user-select: none;
}
.style-option:hover { border-color: #ced4da; }
.style-option.active { border-color: #007bff; box-shadow: 0 0 0 2px rgba(0,123,255,.2); }
.style-label { text-align: center; font-size: .78rem; color: #6c757d; }
.style-option.active .style-label { color: #007bff; font-weight: 600; }

.style-thumb {
	height: 58px;
	border-radius: 4px;
	background: #e9ecef;
	position: relative;
	overflow: hidden;
	margin-bottom: 6px;
}

/* ── BUBBLE: round avatar+name pill on top, rounded content box below ── */
.style-thumb--bubble::before { /* avatar circle sitting on top of pill */
	content: ''; position: absolute;
	width: 16px; height: 16px; border-radius: 50%;
	background: #4CAF50; top: 6px; left: 6px; z-index: 2;
}
.style-thumb--bubble .st-title { /* pill */
	position: absolute; top: 6px; left: 6px;
	height: 16px; width: 55%; background: #4CAF50;
	border-radius: 100px; z-index: 1;
}
.style-thumb--bubble .st-body { /* message bubble, corner cut top-left */
	position: absolute; top: 26px; left: 16px;
	right: 6px; bottom: 6px; background: #fff;
	border-radius: 0 10px 10px 10px;
	box-shadow: 0 1px 3px rgba(0,0,0,.12);
}

/* ── BLOCKS: square avatar top-left, title bar beside it, wide content below ── */
.style-thumb--blocks::before { /* avatar square */
	content: ''; position: absolute; left: 0; top: 0;
	width: 30px; height: 30px; background: #868e96; z-index: 3;
}
.style-thumb--blocks .st-title { /* name bar to the right of avatar */
	position: absolute; left: 30px; top: 0; right: 0;
	height: 18px; background: #4CAF50; z-index: 2;
}
.style-thumb--blocks .st-body { /* content bar, inset half-avatar width */
	position: absolute; left: 15px; top: 18px;
	right: 0; bottom: 0; background: #4CAF50; opacity: .5; z-index: 2;
}

/* ── MINIMAL: dark bg, colored left bar, square avatar, text lines ── */
.style-thumb--minimal { background: #2c2c30; }
.style-thumb--minimal::before { /* left accent bar */
	content: ''; position: absolute; left: 0; top: 0; bottom: 0;
	width: 5px; background: #4CAF50;
}
.style-thumb--minimal .st-title { /* square avatar */
	position: absolute; left: 12px;
	top: 50%; transform: translateY(-50%);
	width: 26px; height: 26px; background: #555; border-radius: 3px;
}
.style-thumb--minimal .st-body { /* name text line */
	position: absolute; left: 46px; right: 8px; top: 16px;
	height: 8px; background: #666; border-radius: 2px;
}
.style-thumb--minimal .st-body::after { /* message text line */
	content: ''; display: block; margin-top: 6px;
	height: 8px; width: 65%; background: #555; border-radius: 2px;
}

/* ── CLASSIC: circle avatar with color ring, stepped title/content bars ── */
.style-thumb--classic::before { /* colored ring */
	content: ''; position: absolute;
	left: 3px; top: 2px; width: 28px; height: 28px;
	border-radius: 50%; background: #4CAF50; z-index: 2;
}
.style-thumb--classic::after { /* avatar circle inside ring */
	content: ''; position: absolute;
	left: 6px; top: 5px; width: 22px; height: 22px;
	border-radius: 50%; background: #868e96; z-index: 3;
}
.style-thumb--classic .st-title { /* name bar, narrower than content */
	position: absolute; left: 18px; top: 8px;
	right: 14px; height: 14px;
	background: #4CAF50; border-radius: 0 3px 0 0; z-index: 1;
}
.style-thumb--classic .st-body { /* content bar, full width */
	position: absolute; left: 18px; top: 22px;
	right: 0; bottom: 4px;
	background: #4CAF50; opacity: .45; border-radius: 3px; z-index: 1;
}
</style>
